/**
 * useCommunityQuery - Hook para dados da comunidade
 * 
 * Encapsula chamadas ao communityService com cache do React Query.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import communityService from '../../services/communityService';

const POSTS_PER_PAGE = 10;

// ==================== QUERY KEYS ====================
export const communityKeys = {
    all: ['community'],
    posts: () => [...communityKeys.all, 'posts'],
    postsWithFilters: (filters) => [...communityKeys.posts(), filters],
    infinitePosts: (filters) => [...communityKeys.posts(), 'infinite', filters],
    post: (id) => [...communityKeys.all, 'post', id],
    comments: (postId) => [...communityKeys.all, 'comments', postId],
    // Dados POR-USUÁRIO: o userId entra na chave para isolar o cache persistido
    // entre contas no mesmo aparelho (mesma correção da PerformanceScreen).
    // Os prefixos abaixo (sem userId) servem para invalidação por-prefixo.
    savedAll: () => [...communityKeys.all, 'saved'],
    likesAll: () => [...communityKeys.all, 'likes'],
    savedIdsAll: () => [...communityKeys.all, 'savedIds'],
    savedPosts: (userId) => [...communityKeys.savedAll(), userId ?? 'anon'],
    userLikes: (userId) => [...communityKeys.likesAll(), userId ?? 'anon'],
    userSaved: (userId) => [...communityKeys.savedIdsAll(), userId ?? 'anon'],
};

// ==================== HOOKS ====================

/**
 * Hook para buscar posts com filtros e paginação
 */
export const usePosts = (filters = {}, enabled = true) => {
    return useQuery({
        queryKey: communityKeys.postsWithFilters(filters),
        queryFn: () => communityService.getPosts(filters),
        staleTime: 5 * 60 * 1000, // 5 minutos (comunidade educacional — volume moderado)
        enabled,
    });
};

/**
 * Feed paginado (scroll infinito). A queryKey inclui os filtros (tópico/busca),
 * então trocar de tópico/busca refaz a query automaticamente. getPosts usa
 * paginação KEYSET (cursor {createdAt, id}) e retorna { posts, hasMore, nextCursor }.
 */
export const useInfinitePosts = (filters = {}, enabled = true) => {
    return useInfiniteQuery({
        queryKey: communityKeys.infinitePosts(filters),
        queryFn: ({ pageParam }) =>
            communityService.getPosts({ ...filters, limit: POSTS_PER_PAGE, cursor: pageParam }),
        initialPageParam: null,
        getNextPageParam: (lastPage) =>
            lastPage?.hasMore ? lastPage.nextCursor : undefined,
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled,
    });
};

/**
 * Posts de um autor específico (aba "Posts" do perfil), com scroll infinito.
 * A queryKey usa o mesmo prefixo `posts()` → invalidações de post (incl. delete)
 * mantêm esta lista e o feed principal coerentes automaticamente.
 */
export const useUserPosts = (userId, enabled = true) => {
    return useInfiniteQuery({
        queryKey: communityKeys.infinitePosts({ authorId: userId }),
        queryFn: ({ pageParam }) =>
            communityService.getPosts({ authorId: userId, limit: POSTS_PER_PAGE, cursor: pageParam }),
        initialPageParam: null,
        getNextPageParam: (lastPage) =>
            lastPage?.hasMore ? lastPage.nextCursor : undefined,
        staleTime: 5 * 60 * 1000, // 5 minutos
        // Sempre revalida ao montar (abrir a aba Posts do perfil). getPosts engole
        // erros como "sucesso vazio" e o cache é PERSISTIDO 24h — sem isto, uma
        // falha transitória (rede/RLS no boot/RPC) "gruda" uma lista vazia e os
        // posts do usuário somem até o staleTime expirar. Custo: 1 round-trip ao
        // abrir o perfil (já é o comportamento esperado de uma aba de conteúdo).
        refetchOnMount: 'always',
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para buscar um post específico por ID
 */
export const usePost = (postId, enabled = true) => {
    return useQuery({
        queryKey: communityKeys.post(postId),
        queryFn: () => communityService.getPostById(postId),
        staleTime: 1 * 60 * 1000, // 1 minuto
        enabled: enabled && !!postId,
    });
};

/**
 * Hook para buscar comentários de um post
 */
export const useComments = (postId, enabled = true) => {
    return useQuery({
        queryKey: communityKeys.comments(postId),
        queryFn: () => communityService.getComments(postId),
        staleTime: 1 * 60 * 1000, // 1 minuto
        enabled: enabled && !!postId,
    });
};

/**
 * Hook para buscar posts salvos do usuário
 */
export const useSavedPosts = (userId, enabled = true) => {
    return useQuery({
        queryKey: communityKeys.savedPosts(userId),
        queryFn: () => communityService.getSavedPosts(),
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para buscar IDs dos posts curtidos pelo usuário
 */
export const useUserLikes = (userId, enabled = true) => {
    return useQuery({
        queryKey: communityKeys.userLikes(userId),
        queryFn: () => communityService.getUserLikes(),
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para buscar IDs dos posts salvos pelo usuário (leve — para o feed).
 */
export const useUserSaved = (userId, enabled = true) => {
    return useQuery({
        queryKey: communityKeys.userSaved(userId),
        queryFn: () => communityService.getUserSavedPostIds(),
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled: enabled && !!userId,
    });
};

// ==================== MUTATIONS ====================

/**
 * Hook para criar um novo post
 */
export const useCreatePost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (postData) => communityService.createPost(postData),
        onSuccess: () => {
            // Invalida cache de posts para refetch
            queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
        },
    });
};

/**
 * Hook para votar em um post
 */
export const useVotePost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ postId, voteType }) => communityService.votePost(postId, voteType),
        onSuccess: (_, { postId }) => {
            // Invalida cache do post específico
            queryClient.invalidateQueries({ queryKey: communityKeys.post(postId) });
            queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
        },
    });
};

/**
 * Hook para curtir/descurtir um post
 */
export const useToggleLike = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (postId) => communityService.toggleLikePost(postId),
        onSuccess: (_, postId) => {
            queryClient.invalidateQueries({ queryKey: communityKeys.post(postId) });
            // Prefixo (sem userId) → casa a chave de likes de qualquer usuário ativo.
            queryClient.invalidateQueries({ queryKey: communityKeys.likesAll() });
            // Sem isto, o contador de likes no feed/lista fica stale até o
            // staleTime expirar (paridade com useVotePost).
            queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
        },
    });
};

/**
 * Hook para salvar/dessalvar um post
 */
export const useToggleSave = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (postId) => communityService.toggleSavePost(postId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: communityKeys.savedAll() });
        },
    });
};

/**
 * Apagar um post (somente o autor — autorização real na RLS do Postgres).
 * Otimista: remove o item de TODAS as listas de posts em cache (perfil + feed)
 * com rollback em erro; `onSettled` reconcilia com o servidor.
 */
export const useDeletePost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (postId) => {
            const result = await communityService.deletePost(postId);
            if (!result?.success) {
                throw new Error(result?.error || 'Não foi possível apagar o post.');
            }
            return result;
        },
        onMutate: async (postId) => {
            await queryClient.cancelQueries({ queryKey: communityKeys.posts() });
            const previous = queryClient.getQueriesData({ queryKey: communityKeys.posts() });

            queryClient.setQueriesData({ queryKey: communityKeys.posts() }, (old) => {
                // Só toca caches infinitos ({ pages }); os demais são reconciliados no settle.
                if (!old?.pages) return old;
                return {
                    ...old,
                    pages: old.pages.map((page) => ({
                        ...page,
                        posts: (page.posts ?? []).filter((post) => post.id !== postId),
                    })),
                };
            });

            return { previous };
        },
        onError: (_error, _postId, context) => {
            context?.previous?.forEach(([queryKey, data]) => {
                queryClient.setQueryData(queryKey, data);
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
        },
    });
};

/**
 * Hook para adicionar comentário
 */
export const useAddComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ postId, content, parentId }) =>
            communityService.addComment(postId, content, parentId),
        onSuccess: (_, { postId }) => {
            queryClient.invalidateQueries({ queryKey: communityKeys.comments(postId) });
            queryClient.invalidateQueries({ queryKey: communityKeys.post(postId) });
        },
    });
};
